import { useLingui } from '@lingui/react/macro';
import CognitiveLearningIcon from '../../icons/cognitive-learning.svg?react';
import ColorBlindnessIcon from '../../icons/color-blindness.svg?react';
import DyslexiaIcon from '../../icons/dyslexia.svg?react';
import KeyboardNavigationIcon from '../../icons/keyboard-navigation.svg?react';
import LowVisionIcon from '../../icons/low-vision.svg?react';
import MotorAssistanceIcon from '../../icons/motor-assistance.svg?react';
import ReduceDistractionsIcon from '../../icons/reduce-distractions.svg?react';
import SeizureSafeIcon from '../../icons/seizure-safe.svg?react';
import type { Mode, ModeData } from '../../types';

export const useModeData = (): Record<Mode, ModeData> => {
  const { t } = useLingui();

  return {
    'low-vision': {
      label: t`Low Vision`,
      description: t`Enlarges text and increases contrast to help people with low vision read content more easily`,
      icon: LowVisionIcon,
      settings: {
        largeText: true,
        extraTextSpacing: true,
        highlightLinks: true,
        largeClickableAreas: true,
        bigCursor: true,
        colorsContrast: 'high-contrast'
      }
    },
    'color-blindness': {
      label: t`Color Blindness`,
      description: t`Adjusts colors and improves visibility to help people with color blindness better distinguish elements`,
      icon: ColorBlindnessIcon,
      settings: {
        highlightLinks: true,
        colorsContrast: 'color-blind'
      }
    },
    'dyslexia-friendly': {
      label: t`Dyslexia-Friendly`,
      description: t`Applies a special font and extra spacing to help people with dyslexia read with less strain`,
      icon: DyslexiaIcon,
      settings: {
        largeText: true,
        extraTextSpacing: true,
        stopAnimation: true,
        dyslexiaFriendlyFont: true
      }
    },
    'seizure-safe': {
      label: t`Seizure Safe`,
      description: t`Disables animations and flashing effects to help people with epilepsy or motion sensitivity stay safe`,
      icon: SeizureSafeIcon,
      settings: {
        stopAnimation: true
      }
    },
    'reduce-distractions': {
      label: t`Reduce Distractions`,
      description: t`Applies a reading mask and stops animations to help people with ADHD or sensory sensitivity stay focused while reading`,
      icon: ReduceDistractionsIcon,
      settings: {
        stopAnimation: true,
        readingMask: true
      }
    },
    'motor-assistance': {
      label: t`Motor Assistance`,
      description: t`Enlarges interactive elements and enables keyboard control to help people with limited motor skills navigate the site`,
      icon: MotorAssistanceIcon,
      settings: {
        highlightLinks: true,
        largeClickableAreas: true,
        bigCursor: true,
        keyboardFocus: true
      }
    },
    'cognitive-and-learning': {
      label: t`Cognitive & Learning`,
      description: t`Simplifies text, reduces distractions, and highlights key elements to help people with cognitive or learning difficulties stay focused and better understand content`,
      icon: CognitiveLearningIcon,
      settings: {
        largeText: true,
        extraTextSpacing: true,
        easyReadFont: true,
        highlightLinks: true,
        stopAnimation: true,
        readingLine: true
      }
    },
    'keyboard-navigation': {
      label: t`Keyboard Navigation`,
      description: t`Highlights elements and supports tab control to help people who navigate with a keyboard move through the site`,
      icon: KeyboardNavigationIcon,
      settings: {
        highlightLinks: true,
        keyboardFocus: true
      }
    }
  };
};
